/**
 * Utility funkce pro práci s daty v CV aplikaci
 * Zajišťuje konzistentní formátování dat mezi různými formáty
 */

/**
 * Konvertuje rok a měsíc na formát YYYY-MM pro month inputy
 */
export function formatForMonthInput(year?: number, month?: number): string {
  if (!year) return '';

  // Pokud máme jen rok (např. z Bright Data), vratíme formát YYYY-01
  if (!month || month < 1 || month > 12) {
    return `${year}-01`;
  }

  // Formátujeme měsíc s padding (01, 02, etc.)
  const paddedMonth = month.toString().padStart(2, '0');
  return `${year}-${paddedMonth}`;
}

/**
 * Parsuje formát YYYY-MM z month inputu na objekty
 */
export function parseMonthInput(dateString: string): { year?: number; month?: number } {
  if (!dateString || typeof dateString !== 'string') {
    return {};
  }

  const parts = dateString.split('-');
  if (parts.length !== 2) {
    return {};
  }

  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]);

  return {
    year: isNaN(year) ? undefined : year,
    month: isNaN(month) || month < 1 || month > 12 ? undefined : month,
  };
}

/**
 * Konvertuje CVExperience formát na Experience formát
 */
export function convertCVExperienceToExperience(cvExperience: {
  title?: string;
  company?: string;
  startDate?: number;
  startMonth?: number;
  endDate?: number;
  endMonth?: number;
  description?: string;
}): {
  title: string;
  company: string;
  startDate: string;
  endDate: string;
  description: string;
} {
  return {
    title: cvExperience.title || '',
    company: cvExperience.company || '',
    startDate: formatForMonthInput(cvExperience.startDate, cvExperience.startMonth),
    endDate: formatForMonthInput(cvExperience.endDate, cvExperience.endMonth),
    description: cvExperience.description || '',
  };
}

/**
 * Konvertuje CVEducation formát na Education formát
 */
export function convertCVEducationToEducation(cvEducation: {
  school?: string;
  degree?: string;
  startDate?: number;
  startMonth?: number;
  endDate?: number;
  endMonth?: number;
}): {
  school: string;
  degree: string;
  startDate: string;
  endDate: string;
} {
  return {
    school: cvEducation.school || '',
    degree: cvEducation.degree || '',
    startDate: formatForMonthInput(cvEducation.startDate, cvEducation.startMonth),
    endDate: formatForMonthInput(cvEducation.endDate, cvEducation.endMonth),
  };
}

/**
 * Formátuje datum pro zobrazení v preview (např. "Leden 2023")
 */
export function formatDateForDisplay(dateString: string, locale: string = 'en'): string {
  if (!dateString || typeof dateString !== 'string') return '';

  // Pokud je datum ve formátu YYYY-MM, přidáme první den
  const fullDate =
    dateString.includes('-') && dateString.split('-').length === 2
      ? `${dateString}-01`
      : dateString;

  const date = new Date(fullDate);
  if (isNaN(date.getTime())) return dateString;

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
  };

  const normalizedLocale = locale && typeof locale === 'string' ? locale : 'en';

  try {
    return date.toLocaleDateString(normalizedLocale, options);
  } catch {
    try {
      return date.toLocaleDateString(normalizedLocale.split('-')[0] || 'en', options);
    } catch {
      return date.toLocaleDateString('en', options);
    }
  }
}

/**
 * Formátuje datum narození (YYYY-MM-DD) na plné lokalizované datum, např. "12. 5. 1990".
 * Neplatný nebo volný text vrací beze změny.
 */
export function formatDateOfBirth(dateString: string | undefined, locale: string = 'en'): string {
  if (!dateString || typeof dateString !== 'string') return '';
  const match = dateString.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dateString;

  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (isNaN(date.getTime())) return dateString;

  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' };
  const normalizedLocale = locale && typeof locale === 'string' ? locale : 'en';
  try {
    return date.toLocaleDateString(normalizedLocale, options);
  } catch {
    try {
      return date.toLocaleDateString(normalizedLocale.split('-')[0] || 'en', options);
    } catch {
      return date.toLocaleDateString('en', options);
    }
  }
}

/**
 * Formátuje období pro zobrazení (např. "Leden 2023 - Současnost")
 */
export interface FormatPeriodOptions {
  locale?: string;
  presentLabel?: string;
  delimiter?: string;
}

export function formatPeriodForDisplay(
  startDate: string,
  endDate: string,
  { locale = 'en', presentLabel = 'Present', delimiter = ' - ' }: FormatPeriodOptions = {},
): string {
  const validStartDate = typeof startDate === 'string' ? startDate : '';
  const validEndDate = typeof endDate === 'string' ? endDate : '';

  if (!validStartDate && !validEndDate) {
    return '';
  }

  const start = validStartDate ? formatDateForDisplay(validStartDate, locale) : '';
  const end = validEndDate ? formatDateForDisplay(validEndDate, locale) : '';

  const safePresentLabel = presentLabel || 'Present';
  const safeDelimiter = delimiter ?? ' - ';

  if (!validEndDate) {
    return start ? `${start}${safeDelimiter}${safePresentLabel}` : '';
  }

  if (!validStartDate) {
    return end || validEndDate;
  }

  if (!end) {
    return start || validStartDate;
  }

  if (!start) {
    return end || validEndDate;
  }

  return `${start}${safeDelimiter}${end}`;
}

/**
 * Validuje a opravuje datum z různých zdrojů (Bright Data, uživatelský input, etc.)
 */
export function normalizeDate(dateInput: string | number | undefined): string {
  if (!dateInput) return '';

  // Pokud je to číslo (rok), převedeme na formát YYYY-01
  if (typeof dateInput === 'number') {
    return `${dateInput}-01`;
  }

  // Pokud je to string
  if (typeof dateInput === 'string') {
    // Pokud obsahuje už měsíc, vracíme jak je
    if (dateInput.includes('-')) {
      return dateInput;
    }

    // Pokud je to jen rok, přidáme měsíc
    const year = parseInt(dateInput);
    if (!isNaN(year) && year > 1900 && year < 2100) {
      return `${year}-01`;
    }
  }

  return '';
}
